/**
 * 📐 DASHBOARD ENGINE - LAYOUT BLUEPRINT v2.0
 * Standardizes the spatial coordinates for the Hybrid Scoped Command Center.
 * Personal layouts are stored in /users/{uid}/dashboardLayout.
 */
import { WidgetLayout, WidgetId } from '@/types';

/**
 * 📐 DEFAULT DASHBOARD REGISTRY
 * Refactored for Hybrid Scoping: Filters modules based on tenancy status.
 */
export const getInitialLayout = (hasHousehold: boolean): WidgetLayout[] => {
  const baseLayout: WidgetLayout[] = [
    {
      i: 'quick_access',
      x: 0,
      y: 0,
      w: 16,
      h: 5,
    },
    {
      i: 'sparks',
      x: 16,
      y: 0,
      w: 16,
      h: 5,
    },
    {
      i: 'calendar_agenda',
      x: 32,
      y: 0,
      w: 16,
      h: 14,
    },
    {
      i: 'tasks_summary',
      x: 0,
      y: 5,
      w: 16,
      h: 9,
    }
  ];

  const householdWidgets: WidgetLayout[] = [
    {
      i: 'maintenance_agenda',
      x: 16,
      y: 5,
      w: 16,
      h: 5,
    },
    {
      i: 'activity_log',
      x: 16,
      y: 10,
      w: 16,
      h: 4,
    }
  ];

  return hasHousehold ? [...baseLayout, ...householdWidgets] : baseLayout;
};

// Legacy support for direct import
export const DEFAULT_LAYOUTS: WidgetLayout[] = getInitialLayout(true);

/**
 * 🛠️ WIDGET CONSTRAINTS
 * Defines physical boundaries for resizing logic.
 * Heights adjusted (+1) to accommodate Hybrid Scope Header Badges.
 */
export const WIDGET_CONSTRAINTS: Record<WidgetId, { minW: number; minH: number; maxW?: number; maxH?: number }> = {
  quick_access: { minW: 8, minH: 3, maxW: 20 },
  calendar_agenda: { minW: 10, minH: 6 },
  maintenance_agenda: { minW: 10, minH: 4 },
  sparks: { minW: 8, minH: 4, maxW: 24 },
  tasks_summary: { minW: 10, minH: 6 },
  activity_log: { minW: 10, minH: 4, maxH: 14 },
  bills_summary: { minW: 8, minH: 4 },
  pets_overview: { minW: 8, minH: 5 },
  vehicles_summary: { minW: 8, minH: 4 },
  leaderboard: { minW: 8, minH: 4, maxW: 20 },
  daily_brief: { minW: 8, minH: 5, maxW: 28 },
  health_score: { minW: 6, minH: 4, maxW: 14 },
  finance_summary: { minW: 8, minH: 5 },
  meal_planner_week: { minW: 10, minH: 5 },
  co_parenting_summary: { minW: 8, minH: 6 },
  elder_care_summary: { minW: 8, minH: 6 },
  appliance_overview: { minW: 8, minH: 5 },
  location_overview: { minW: 8, minH: 5 },
};

/**
 * 🛰️ getConstraintById
 * Utility to safely fetch constraints. 
 */
export const getConstraintById = (id: string) => {
  return (WIDGET_CONSTRAINTS as any)[id] || { minW: 4, minH: 4 };
};

/**
 * 📐 calculateResponsiveLayout
 * Refactored to ensure data-dense widgets maintain readable widths during 
 * Hybrid Scope toggles (Personal/Shared/Both).
 */
export const getResponsiveLayout = (layout: WidgetLayout[], cols: number): WidgetLayout[] => {
  return layout.map(widget => {
    // Determine if widget should be full width on mobile/small breakpoints
    const isMobile = cols < 12;
    return {
      ...widget,
      w: isMobile ? cols : Math.min(widget.w, cols),
      x: widget.x >= cols ? 0 : widget.x
    };
  });
};